import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUploadCloud, FiSave, FiX, FiMapPin, FiTag, FiPackage, FiDownloadCloud, FiPlus, FiTrash2, FiZap, FiLoader, FiChevronLeft, FiChevronRight, FiStar, FiAlertTriangle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ServiceLocationPicker from '../../components/merchant/ServiceLocationPicker';
import DealCitiesPicker from '../../components/merchant/DealCitiesPicker';
import { validateImageFiles } from '../../utils/imageValidation';
import { dominantColorFromUrl, assignColorsToImages } from '../../utils/colorDetect';

const MAX_IMAGES = 8;

// ===== Caractéristiques intelligentes (produits physiques) =====
// Selon la nature du produit, le marchand active les bonnes caractéristiques.
const ATTR_DEFS = {
  size:     { label: 'Taille', presets: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'], kind: 'list', ph: 'Ex. 42, Unique…' },
  shoe:     { label: 'Pointure', presets: ['36', '37', '38', '39', '40', '41', '42', '43', '44', '45'], kind: 'list', ph: 'Ex. 47' },
  color:    { label: 'Couleur', presets: ['Noir', 'Blanc', 'Gris', 'Rouge', 'Bleu', 'Vert', 'Jaune', 'Rose', 'Orange', 'Marron', 'Beige', 'Violet'], kind: 'color', ph: 'Ex. Bordeaux' },
  volume:   { label: 'Volume / contenance', presets: ['100', '250', '500', '750', '1000'], units: ['ml', 'cl', 'L'], kind: 'measure', ph: 'Ex. 330' },
  weight:   { label: 'Poids / grammage', presets: ['100', '250', '500', '1000', '2000', '5000'], units: ['g', 'kg'], kind: 'measure', ph: 'Ex. 750' },
  capacity: { label: 'Capacité / stockage', presets: ['32', '64', '128', '256', '512', '1024'], units: ['Go', 'To'], kind: 'measure', ph: 'Ex. 128' },
  material: { label: 'Matière', presets: ['Coton', 'Cuir', 'Bois', 'Métal', 'Plastique', 'Verre', 'Inox', 'Laine'], kind: 'list', ph: 'Ex. Velours' },
  custom:   { label: 'Autre (personnalisé)', presets: [], kind: 'free', ph: 'Ex. Pack 3 pièces' },
};
const ATTR_ORDER = ['size', 'shoe', 'color', 'volume', 'weight', 'capacity', 'material', 'custom'];
const COLOR_HEX = {
  Noir: '#111827', Blanc: '#f3f4f6', Gris: '#9ca3af', Rouge: '#ef4444', Bleu: '#3b82f6',
  Vert: '#22c55e', Jaune: '#eab308', Rose: '#ec4899', Orange: '#f97316', Marron: '#92400e',
  Beige: '#e7d3b3', Violet: '#8b5cf6'
};
const attrFmt = (type, unit, val) => (ATTR_DEFS[type] && ATTR_DEFS[type].kind === 'measure') ? `${val}${unit || ''}` : String(val);

// ✅ small helper (so you don't need a separate cityAPI file)
const fetchCitiesWithFallback = async () => {
  // try multiple endpoints to match your backend variations
  const tries = [
    { url: '/cities', params: { active: true } },
    { url: '/admin/cities', params: { active: true } },
    { url: '/cities/active', params: {} },
  ];

  let lastErr = null;

  for (const t of tries) {
    try {
      const res = await API.get(t.url, { params: t.params });
      const data = res?.data;
      const list = data?.data || data?.cities || data || [];
      if (Array.isArray(list)) return list;
    } catch (e) {
      lastErr = e;
    }
  }

  throw lastErr || new Error('Failed to load cities');
};

// Selecteur de marque avec logo (catalogue admin + saisie libre en secours)
import BrandPicker from '../../components/merchant/BrandPicker';
import PlanQuotaBanner from '../../components/merchant/PlanQuotaBanner';
import QuantityTiersEditor from '../../components/merchant/QuantityTiersEditor';
import InfoSectionsEditor from '../../components/merchant/InfoSectionsEditor';

const CreateDeal = () => {
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const { user } = useAuth();

  const [categories, setCategories] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [cities, setCities] = useState([]); // ✅ ADDED
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const [formData, setFormData] = useState({
    productType: 'service', // 'service' | 'physical' | 'digital'
    title: '',
    description: '',
    originalPrice: '',
    serviceLocationIds: [],
    partnerPrice: '',
    price: '',
    category: '',
    validFrom: '',
    validUntil: '',
    openEnded: false, // ✅ validité ouverte (sans date de fin)
    quantity: '',
    unlimited: false,
    quantityTiers: [],   // prix degressif : [{ minQty, discountPercent }]
    infoSections: [],   // sections libres affichees en onglets
    // ✅ location removed from UI, but we keep compatibility in payload using selected city name
    city: '',            // ✅ ADDED
    terms: '',
    // ===== Lot P1 — Infos de prestation (surtout services) =====
    // addressFull / instructions ne sont JAMAIS publics : ils sont communiqués à
    // l'acheteur sur son coupon, après paiement uniquement.
    addressFull: '',
    addressLat: '',
    addressLng: '',
    openingHours: '',
    durationMinutes: '',
    instructions: '',
    // Lieu de prestation reutilisable : '' = saisie manuelle propre a cette annonce
    serviceLocationId: '',
    saveAsServiceLocation: false,
    serviceLocationLabel: '',
    highlights: [''],
    images: [], // File objects
    merchantId: '',
    // ===== Produit physique =====
    shippingFee: '',
    freeShippingThreshold: '',
    weightGrams: '',
    variants: [], // [{ label, priceDelta, stock }]
    attributes: [], // [{ type, unit, values:[] }] — schéma caractéristiques
    shippingZones: [], // [{ name, cities:[], fee, freeThreshold }]
    shippingMode: 'flat', // Lot D : 'flat' | 'weight'
    shippingWeightTiers: [], // [{ uptoGrams, fee }]
    colorImageMap: {}, // { "Rouge": 2 } -> index image par couleur
    // ===== Fiche produit =====
    brandId: null,             // marque du catalogue (null = saisie libre)
    condition: 'new',          // 'new' | 'used' (produits physiques uniquement)
    availability: 'in_stock',  // 'in_stock' | 'out_of_stock' | 'incoming'
    brand: '',
    sku: '',
    barcode: '',
    expiryDate: ''             // compléments alimentaires, cosmétiques...
  });

  const [errors, setErrors] = useState({});
  const [attrInputs, setAttrInputs] = useState({}); // saisie libre par type de caractéristique
  const imagePreviewsRef = useRef([]);

  useEffect(() => {
    mountedRef.current = true;
    fetchCategories();
    fetchMerchantsIfAdmin();
    loadCities(); // ✅ ADDED

    return () => {
      mountedRef.current = false;
      imagePreviewsRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} });
      imagePreviewsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCities = async () => {
    try {
      const list = await fetchCitiesWithFallback();
      if (!mountedRef.current) return;
      // optional: keep only active if backend returns mixed
      const filtered = list.filter(c => c?.active !== false);
      setCities(filtered);
    } catch (error) {
      console.error('Error loading cities:', error);
      if (!mountedRef.current) return;
      setCities([]);
      // optional toast (you can remove if noisy)
      // toast.warn("Impossible de charger les villes");
    }
  };

  // Application d'un lieu de prestation enregistre : on recopie ses infos dans le
  // formulaire. `null` = le marchand repasse en saisie manuelle (champs liberes).
  const applyServiceLocation = (loc) => {
    setFormData((f) => (loc ? {
      ...f,
      serviceLocationId: String(loc.id),
      addressFull: loc.addressFull || '',
      addressLat: loc.addressLat ?? '',
      addressLng: loc.addressLng ?? '',
      openingHours: loc.openingHours || '',
      instructions: loc.instructions || '',
      saveAsServiceLocation: false,
      serviceLocationLabel: ''
    } : { ...f, serviceLocationId: '' }));
    setErrors((e) => ({ ...e, addressFull: undefined }));
  };

  const isAdmin = useMemo(() => {
    if (!user) return false;
    if (typeof user.role === 'string') return user.role.toLowerCase().includes('admin');
    if (Array.isArray(user.roles)) return user.roles.some(r => (typeof r === 'string' ? r : r?.name || '').toLowerCase().includes('admin'));
    if (user.role && typeof user.role.name === 'string') return user.role.name.toLowerCase().includes('admin');
    return false;
  }, [user]);

  // Champs de prestation verrouillés quand ils proviennent d'un lieu enregistré :
  // on les modifie depuis Paramètres > Lieux de prestation, pas annonce par annonce.
  const locked = !isAdmin && !!formData.serviceLocationId;

  const fetchMerchantsIfAdmin = async () => {
    try {
      const endpoints = ['/users/me', '/auth/me', '/me'];
      let me = null;
      for (const ep of endpoints) {
        try {
          const res = await API.get(ep);
          me = res?.data?.data ?? res?.data ?? res;
          if (me) break;
        } catch (e) {}
      }
      if (!me) return;

      const roleStr = ((me.role && (typeof me.role === 'string' ? me.role : me.role.name)) || (Array.isArray(me.roles) && me.roles.join(',')) || '').toLowerCase();
      if (roleStr.includes('admin')) {
        try {
          const { data } = await API.get('/admin/merchants');
          if (!mountedRef.current) return;
          setMerchants(data?.data || data || []);
        } catch (err) {
          try {
            const { data } = await API.get('/merchants');
            if (!mountedRef.current) return;
            setMerchants(data?.data || data || []);
          } catch (e) { console.warn('no merchants', e); }
        }
      }
    } catch (e) { console.warn(e); }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await API.get('/categories');
      if (!mountedRef.current) return;
      setCategories(data?.data || data || []);
    } catch (err) {
      console.error(err);
      toast.error('Erreur chargement catégories');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleHighlightChange = (i, v) => {
    const arr = [...formData.highlights];
    arr[i] = v;
    setFormData(prev => ({ ...prev, highlights: arr }));
  };

  const addHighlight = () => setFormData(prev => ({ ...prev, highlights: [...prev.highlights, ''] }));
  const removeHighlight = (i) => setFormData(prev => ({ ...prev, highlights: prev.highlights.filter((_, idx) => idx !== i) }));

  // ===== Variantes (produit physique) =====
  const addVariant = () => setFormData(prev => ({ ...prev, variants: [...(prev.variants || []), { label: '', priceDelta: '', stock: '' }] }));
  const removeVariant = (i) => setFormData(prev => ({ ...prev, variants: (prev.variants || []).filter((_, idx) => idx !== i) }));
  const updateVariant = (i, key, val) => setFormData(prev => {
    const arr = [...(prev.variants || [])];
    arr[i] = { ...arr[i], [key]: val };
    return { ...prev, variants: arr };
  });

  // ===== Caractéristiques intelligentes =====
  const getAttr = (type) => (formData.attributes || []).find(a => a.type === type);
  const toggleAttribute = (type) => setFormData(prev => {
    const list = prev.attributes || [];
    if (list.some(a => a.type === type)) return { ...prev, attributes: list.filter(a => a.type !== type) };
    const def = ATTR_DEFS[type];
    return { ...prev, attributes: [...list, { type, unit: def.units ? def.units[0] : null, values: [] }] };
  });
  const setAttrUnit = (type, unit) => setFormData(prev => ({ ...prev, attributes: (prev.attributes || []).map(a => a.type === type ? { ...a, unit } : a) }));
  const addAttrValue = (type, raw) => {
    const val = String(raw || '').trim();
    if (!val) return;
    setFormData(prev => ({
      ...prev,
      attributes: (prev.attributes || []).map(a => (a.type === type && !a.values.includes(val)) ? { ...a, values: [...a.values, val] } : a)
    }));
  };
  const removeAttrValue = (type, val) => setFormData(prev => ({
    ...prev,
    attributes: (prev.attributes || []).map(a => a.type === type ? { ...a, values: a.values.filter(v => v !== val) } : a)
  }));
  const generateVariants = () => {
    const active = (formData.attributes || []).filter(a => a.values && a.values.length);
    if (!active.length) { toast.error('Activez au moins une caractéristique et ajoutez des valeurs'); return; }
    const dims = active.map(a => a.values.map(v => attrFmt(a.type, a.unit, v)));
    let combos = [[]];
    dims.forEach(vals => {
      const next = [];
      combos.forEach(c => vals.forEach(v => next.push([...c, v])));
      combos = next;
    });
    if (combos.length > 60) { toast.error(`Trop de combinaisons (${combos.length}). Limite : 60.`); return; }
    const prevByLabel = {};
    (formData.variants || []).forEach(v => { prevByLabel[v.label] = v; });
    const variants = combos.map(parts => {
      const label = parts.join(' / ');
      const ex = prevByLabel[label];
      return { label, priceDelta: ex ? ex.priceDelta : '', stock: ex ? ex.stock : '' };
    });
    setFormData(prev => ({ ...prev, variants }));
    toast.success(`${variants.length} variante(s) générée(s)`);
  };

  // ===== Zones de livraison =====
  const addZone = () => setFormData(prev => ({ ...prev, shippingZones: [...(prev.shippingZones || []), { name: '', cities: [], fee: '', freeThreshold: '' }] }));
  const removeZone = (i) => setFormData(prev => ({ ...prev, shippingZones: (prev.shippingZones || []).filter((_, idx) => idx !== i) }));
  const updateZone = (i, key, val) => setFormData(prev => {
    const arr = [...(prev.shippingZones || [])];
    arr[i] = { ...arr[i], [key]: val };
    return { ...prev, shippingZones: arr };
  });
  const updateZoneCities = (i, raw) => setFormData(prev => {
    const arr = [...(prev.shippingZones || [])];
    arr[i] = { ...arr[i], cities: String(raw).split(',').map(s => s.trim()) };
    return { ...prev, shippingZones: arr };
  });

  // ===== Paliers de poids (Lot D) =====
  const addTier = () => setFormData(prev => ({ ...prev, shippingWeightTiers: [...(prev.shippingWeightTiers || []), { uptoGrams: '', fee: '' }] }));
  const removeTier = (i) => setFormData(prev => ({ ...prev, shippingWeightTiers: (prev.shippingWeightTiers || []).filter((_, idx) => idx !== i) }));
  const updateTier = (i, key, val) => setFormData(prev => {
    const arr = [...(prev.shippingWeightTiers || [])];
    arr[i] = { ...arr[i], [key]: val };
    return { ...prev, shippingWeightTiers: arr };
  });

  // ===== Image par couleur =====
  const setColorImage = (color, idx) => setFormData(prev => {
    const map = { ...(prev.colorImageMap || {}) };
    if (idx == null) delete map[color]; else map[color] = idx;
    return { ...prev, colorImageMap: map };
  });

  // Effacer des associations couleur->image (liste de couleurs, ou toutes si non fourni)
  const clearColorImages = (colorsToClear) => setFormData((prev) => {
    const map = { ...(prev.colorImageMap || {}) };
    (colorsToClear && colorsToClear.length ? colorsToClear : Object.keys(map)).forEach((c) => { delete map[c]; });
    return { ...prev, colorImageMap: map };
  });

  // Détection auto : couleur dominante de chaque image -> association à la bonne couleur
  const [detecting, setDetecting] = useState(false);
  const [detectProgress, setDetectProgress] = useState(0);
  const [lowConfColors, setLowConfColors] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  // Réordonnancement des images : déplace l'image et remappe colorImageMap (les couleurs suivent leurs images)
  const reorderImages = (from, to) => {
    if (from == null || to == null || from === to) return;
    setFormData((prev) => {
      const imgs = [...(prev.images || [])];
      if (from < 0 || to < 0 || from >= imgs.length || to >= imgs.length) return prev;
      const order = imgs.map((_, i) => i);
      const [movedImg] = imgs.splice(from, 1); imgs.splice(to, 0, movedImg);
      const [movedOrd] = order.splice(from, 1); order.splice(to, 0, movedOrd);
      const pv = imagePreviewsRef.current || [];
      const [movedPv] = pv.splice(from, 1); pv.splice(to, 0, movedPv);
      imagePreviewsRef.current = pv;
      const inv = {}; order.forEach((oldI, newI) => { inv[oldI] = newI; });
      const map = {};
      Object.entries(prev.colorImageMap || {}).forEach(([c, oi]) => { const ni = inv[Number(oi)]; if (ni != null) map[c] = ni; });
      return { ...prev, images: imgs, colorImageMap: map };
    });
  };
  const autoDetectColors = async () => {
    const colorAttr = (formData.attributes || []).find((a) => a.type === 'color' && Array.isArray(a.values) && a.values.length);
    const imgs = formData.images || [];
    if (!colorAttr || imgs.length === 0) { toast.info('Ajoutez des images et au moins une couleur d\'abord.'); return; }
    setDetecting(true); setDetectProgress(0); setLowConfColors([]);
    try {
      const urls = imgs.map((_, i) => {
        let u = imagePreviewsRef.current[i];
        if (!u && imgs[i]) { try { u = URL.createObjectURL(imgs[i]); imagePreviewsRef.current[i] = u; } catch (e) { u = ''; } }
        return u || '';
      });
      let done = 0;
      const imageColors = await Promise.all(urls.map((u) => dominantColorFromUrl(u).then((c) => { done += 1; setDetectProgress(Math.round((done / urls.length) * 100)); return c; })));
      const { map, unresolved, lowConfidence } = assignColorsToImages(colorAttr.values, imageColors, COLOR_HEX);
      if (Object.keys(map).length) {
        setFormData((prev) => ({ ...prev, colorImageMap: { ...map } }));
        setLowConfColors(lowConfidence || []);
        const n = Object.keys(map).length;
        toast.success(`\u2713 ${n} image${n > 1 ? 's' : ''} class\u00e9e${n > 1 ? 's' : ''} automatiquement.`);
      } else {
        toast.info('Aucune couleur n\'a pu être détectée automatiquement.');
      }
      if (lowConfidence && lowConfidence.length) toast.info(`\u26a0 Confiance faible, \u00e0 v\u00e9rifier : ${lowConfidence.join(', ')}.`);
      if (unresolved.length) toast.info(`À associer manuellement : ${unresolved.join(', ')}.`);
    } finally {
      setDetecting(false);
      setTimeout(() => setDetectProgress(0), 700);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = null;
    if (!files.length) return;
    const existing = formData.images || [];
    const spaceLeft = Math.max(0, MAX_IMAGES - existing.length);
    const { valid, rejected } = await validateImageFiles(files.slice(0, spaceLeft));
    rejected.forEach((r) => toast.warn(`${r.name} ignoré : ${r.reason}`));
    if (!valid.length) return;
    const previews = valid.map(f => URL.createObjectURL(f));
    imagePreviewsRef.current = [...imagePreviewsRef.current, ...previews];
    setFormData(prev => ({ ...prev, images: [...existing, ...valid] }));
  };

  const removeImageAt = (index) => {
    const urls = imagePreviewsRef.current;
    const url = urls[index];
    if (url) { try { URL.revokeObjectURL(url); } catch (e) {} }
    imagePreviewsRef.current = urls.filter((_, i) => i !== index);
    setFormData(prev => {
      const map = { ...(prev.colorImageMap || {}) };
      Object.keys(map).forEach((c) => {
        const v = Number(map[c]);
        if (v === index) delete map[c];        // image supprimée -> mapping retiré
        else if (v > index) map[c] = v - 1;    // décalage des index supérieurs
      });
      return { ...prev, images: prev.images.filter((_, i) => i !== index), colorImageMap: map };
    });
  };

  const calculateDiscount = () => {
    const orig = parseFloat(formData.originalPrice) || 0;
    const cur = parseFloat(formData.price) || 0;
    if (orig > 0 && cur > 0 && cur < orig) return Math.round(((orig - cur) / orig) * 100);
    return 0;
  };

  const validate = () => {
    const e = {};
    if (!formData.title?.trim()) e.title = 'Titre requis';
    if (!formData.description?.trim()) e.description = 'Description requise';

    const orig = Number(formData.originalPrice);
    const price = Number(formData.price);

    if (formData.productType === 'service') {
      // Service/deal : prix original requis + prix deal strictement inférieur (vraie remise)
      if (!Number.isFinite(orig) || orig <= 0) e.originalPrice = 'Prix original invalide';
      if (!Number.isFinite(price) || price <= 0) e.price = 'Prix du deal invalide';
      if (Number.isFinite(orig) && Number.isFinite(price) && price >= orig) e.price = 'Prix du deal doit être plus petit que prix original';
      // Lot P1 : sans adresse exacte, l'acheteur ne peut pas se rendre sur place.
      // Le coupon la lui communiquera après paiement.
      if (!formData.serviceLocationId && !formData.addressFull?.trim()) e.addressFull = 'Adresse exacte requise pour un service (communiquée au client après son achat)';
    } else {
      // Produit physique/digital : un seul prix obligatoire ; prix « avant » optionnel (remise facultative)
      if (!Number.isFinite(price) || price <= 0) e.price = 'Prix invalide';
      if (String(formData.originalPrice) !== '' && Number.isFinite(orig) && orig > 0 && orig <= price) {
        e.originalPrice = 'Le prix avant remise doit être supérieur au prix de vente';
      }
    }

    if (!formData.category) e.category = 'Catégorie requise';

    const isService = formData.productType === 'service';
    const isPhysical = formData.productType === 'physical';

    // Ville : requise pour un service (deal local) ; optionnelle pour produit/digital
    if (isService && !formData.city) e.city = 'Ville requise';

    // Quantité / stock selon le type — requis sauf si « Illimité »
    if (!formData.unlimited && (!formData.quantity || parseInt(formData.quantity, 10) < 1)) {
      e.quantity = isPhysical ? 'Stock invalide' : 'Quantité invalide';
    }

    // Dates de validité : services uniquement
    if (isService) {
      if (!formData.validFrom) e.validFrom = 'Date début requise';
      if (!formData.openEnded) {
        if (!formData.validUntil) e.validUntil = 'Date fin requise';
        if (formData.validFrom && formData.validUntil && new Date(formData.validFrom) > new Date(formData.validUntil)) {
          e.validUntil = 'Date fin doit être après début';
        }
      }
    }

    // Variantes physiques : si une ligne est renseignée, le libellé est requis
    if (isPhysical && Array.isArray(formData.variants)) {
      formData.variants.forEach((v, i) => {
        if (v && !String(v.label || '').trim() && (String(v.priceDelta) !== '' || String(v.stock) !== '')) {
          e[`variant_${i}`] = 'Libellé de variante requis';
        }
      });
    }

    if ((formData.images || []).length === 0) e.images = 'Au moins une image requise';
    if ((formData.images || []).length > MAX_IMAGES) e.images = `Max ${MAX_IMAGES} images`;

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error('Corrige les erreurs'); return; }
    setLoading(true);
    setUploadProgress(null);

    try {
      const payload = new FormData();
      payload.append('productType', formData.productType || 'service');
      payload.append('title', formData.title.trim());
      payload.append('description', formData.description.trim());
      const sellPrice = Number(formData.price || 0);
      const beforePrice = Number(formData.originalPrice || 0);
      // Produit/digital sans remise -> prix « avant » = prix de vente (0% de remise)
      const effectiveOriginal = (formData.productType !== 'service' && (!beforePrice || beforePrice <= sellPrice))
        ? sellPrice : beforePrice;
      payload.append('originalPrice', String(effectiveOriginal));
      payload.append('discountedPrice', String(sellPrice));
      // Villes de disponibilite : un champ par lieu coche.
      (formData.serviceLocationIds || []).forEach((id) => payload.append('serviceLocationIds', id));
      // Envoye SEULEMENT s'il est renseigne : une chaine vide serait lue comme
      // « repasse en mode commission », ce qui est un choix, pas un defaut.
      if (String(formData.partnerPrice).trim() !== '') {
        payload.append('partnerPrice', String(Number(formData.partnerPrice)));
      }
      payload.append('category', formData.category);
      // Quantité : « Illimité » -> 0 (le serveur traite quantityTotal<=0 comme illimité) ; sinon le stock saisi
      const qtyToSend = formData.unlimited ? '0' : String(Math.max(0, parseInt(formData.quantity || '0', 10)));
      payload.append('quantity', qtyToSend);
      // Envoye en JSON dans le multipart : le serveur le renormalise de toute
      // facon (sanitizeQuantityTiers), on ne lui fait pas confiance ici.
      payload.append('quantityTiers', JSON.stringify(formData.quantityTiers || []));
      payload.append('infoSections', JSON.stringify(formData.infoSections || []));

      // ===== Fiche produit (état, disponibilité, références, péremption) =====
      if (formData.productType !== 'service') {
        payload.append('availability', formData.availability || 'in_stock');
        // brandId prime : le serveur recopie le nom depuis la fiche marque.
        if (formData.brandId) payload.append('brandId', String(formData.brandId));
        if (formData.brand.trim()) payload.append('brand', formData.brand.trim());
        if (formData.sku.trim()) payload.append('sku', formData.sku.trim());
        if (formData.productType === 'physical') {
          payload.append('condition', formData.condition || 'new');
          if (formData.barcode.trim()) payload.append('barcode', formData.barcode.trim());
          if (formData.expiryDate) payload.append('expiryDate', formData.expiryDate);
        }
      }

      // ===== Champs spécifiques produit physique =====
      if (formData.productType === 'physical') {
        payload.append('shippingFee', String(Number(formData.shippingFee || 0)));
        if (String(formData.freeShippingThreshold) !== '') {
          payload.append('freeShippingThreshold', String(Number(formData.freeShippingThreshold)));
        }
        if (String(formData.weightGrams) !== '') {
          payload.append('weightGrams', String(parseInt(formData.weightGrams, 10) || 0));
        }
        const variants = (formData.variants || [])
          .map(v => ({ label: String(v.label || '').trim(), priceDelta: Number(v.priceDelta || 0), stock: parseInt(v.stock || '0', 10) || 0 }))
          .filter(v => v.label);
        payload.append('variants', JSON.stringify(variants));
        const attributes = (formData.attributes || [])
          .map(a => ({ type: a.type, unit: a.unit || null, values: (a.values || []).map(v => String(v).trim()).filter(Boolean) }))
          .filter(a => a.type && a.values.length);
        payload.append('attributes', JSON.stringify(attributes));
        const shippingZones = (formData.shippingZones || [])
          .map(z => ({ name: String(z.name || '').trim(), cities: (z.cities || []).map(c => String(c).trim()).filter(Boolean), fee: Number(z.fee || 0), freeThreshold: String(z.freeThreshold) !== '' ? Number(z.freeThreshold) : null }))
          .filter(z => z.name && z.cities.length);
        payload.append('shippingZones', JSON.stringify(shippingZones));
        // Lot D — mode de tarif + paliers au poids
        const shippingMode = formData.shippingMode === 'weight' ? 'weight' : 'flat';
        payload.append('shippingMode', shippingMode);
        if (shippingMode === 'weight') {
          const tiers = (formData.shippingWeightTiers || [])
            .map(t => ({ uptoGrams: parseInt(t.uptoGrams, 10) || 0, fee: Number(t.fee || 0) }))
            .filter(t => t.uptoGrams > 0)
            .sort((a, b) => a.uptoGrams - b.uptoGrams);
          payload.append('shippingWeightTiers', JSON.stringify(tiers));
        }
        payload.append('colorImageMap', JSON.stringify(formData.colorImageMap || {}));
      }

      // ✅ send city id
      payload.append('city', formData.city);

      // ✅ backwards compatibility: also send location as city name
      const selectedCity = (cities || []).find(c => String(c?._id) === String(formData.city));
      payload.append('location', selectedCity?.name || '');

      if (formData.validFrom) payload.append('startDate', new Date(formData.validFrom).toISOString());
      // ✅ POINT 5 : validité ouverte -> pas de date de fin envoyée
      payload.append('openEnded', formData.openEnded ? 'true' : 'false');
      if (!formData.openEnded && formData.validUntil) payload.append('endDate', new Date(formData.validUntil).toISOString());
      payload.append('terms', formData.terms || '');

      // ===== Lot P1 — Infos de prestation =====
      // Envoyées même vides pour permettre l'effacement d'un champ.
      payload.append('addressFull', formData.addressFull || '');
      payload.append('instructions', formData.instructions || '');
      payload.append('openingHours', formData.openingHours || '');
      if (formData.addressLat !== '') payload.append('addressLat', formData.addressLat);
      if (formData.addressLng !== '') payload.append('addressLng', formData.addressLng);
      if (formData.durationMinutes !== '') payload.append('durationMinutes', formData.durationMinutes);
      // Rattachement au lieu de prestation enregistre dans le profil marchand.
      payload.append('serviceLocationId', formData.serviceLocationId || '');
      if (!formData.serviceLocationId && formData.saveAsServiceLocation) {
        payload.append('saveAsServiceLocation', 'true');
        payload.append('serviceLocationLabel', formData.serviceLocationLabel || '');
      }

      const highlights = (formData.highlights || []).map(h => h.trim()).filter(Boolean);
      if (highlights.length) payload.append('highlights', JSON.stringify(highlights));

      (formData.images || []).forEach(file => payload.append('images', file));

      const config = {
        onUploadProgress: (evt) => {
          if (evt && evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            if (mountedRef.current) setUploadProgress(pct);
          }
        }
      };

      let endpoint = '/deals';
      if (isAdmin) {
        if (formData.merchantId) endpoint = `/admin/merchants/${formData.merchantId}/deals`;
        else endpoint = '/admin/deals';
      } else {
        endpoint = '/merchant/deals';
      }

      let res;
      try {
        res = await API.post(endpoint, payload, config);
      } catch (err) {
        if (endpoint !== '/deals') res = await API.post('/deals', payload, config);
        else throw err;
      }

      const data = res?.data ?? res;
      if (data?.success) {
        toast.success(data.message || 'Deal créé');
        if (!mountedRef.current) return;
        navigate(data.redirect || (isAdmin ? '/admin/deals' : '/merchant/deals'));
      } else {
        toast.error(data?.message || 'Erreur création');
        if (data?.errors) setErrors(prev => ({ ...prev, ...data.errors }));
      }
    } catch (err) {
      console.error('create error', err);
      const body = err?.response?.data || {};
      // Refus de quota : le serveur renvoie desormais l'etat du pack et le pack
      // suivant. On le dit en clair et on laisse le message a l'ecran plus
      // longtemps — « limite atteinte » disparu en 3 secondes n'aide personne.
      if (body.code === 'PLAN_LIMIT_DEALS' || body.code === 'PLAN_LIMIT_PHOTOS') {
        const u = body.usage;
        const suite = u && u.nextPlan
          ? ` Passez a ${u.nextPlan.name} (${u.nextPlan.priceMonthly} MAD/mois) depuis Mon Abonnement.`
          : '';
        toast.error((body.message || 'Limite du pack atteinte') + suite, { autoClose: 12000 });
        if (mountedRef.current) { setLoading(false); setUploadProgress(null); }
        return;
      }
      const msg = body.message || err?.message || 'Erreur';
      const srvErrs = err?.response?.data?.errors || {};
      setErrors(prev => ({ ...prev, ...srvErrs }));
      toast.error(msg);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setUploadProgress(null);
      }
    }
  };

  const discount = calculateDiscount();

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Créer un Nouveau Deal</h1>
          <p className="text-gray-600 mt-1">Remplissez les informations de votre offre</p>
        </div>

        {/* En mode compact : ne s'affiche qu'a partir de 80 % du quota. Une
            barre permanente ici serait du bruit sur un formulaire deja long. */}
        <PlanQuotaBanner compact />

        <form onSubmit={handleSubmit} noValidate>
          {/* ===== Sélecteur de type d'annonce ===== */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Type d'annonce</h2>
              <p className="text-sm text-gray-500 mt-1">Choisissez ce que vous souhaitez vendre sur Maxula.</p>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: 'service', icon: FiTag, title: 'Service / Deal', desc: 'Bon à valoir, prestation, expérience (voucher).' },
                { key: 'physical', icon: FiPackage, title: 'Produit physique', desc: 'Article livré : stock, variantes, frais de livraison.' },
                { key: 'digital', icon: FiDownloadCloud, title: 'Produit digital', desc: 'Livré via messagerie sécurisée (lien), traçabilité 30 j.' }
              ].map((t) => {
                const Icon = t.icon;
                const active = formData.productType === t.key;
                return (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() => setFormData((prev) => ({ ...prev, productType: t.key, unlimited: t.key === 'digital' }))}
                    className={`text-left rounded-lg border-2 p-4 transition ${active ? 'border-primary-600 bg-primary-50 ring-1 ring-primary-200' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <Icon className={`text-2xl mb-2 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
                    <p className={`font-semibold ${active ? 'text-primary-700' : 'text-gray-800'}`}>{t.title}</p>
                    <p className="text-xs text-gray-500 mt-1">{t.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Informations Principales</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg ${errors.title ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className={`w-full px-4 py-2 border rounded-lg ${errors.description ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg ${errors.category ? 'border-red-300' : 'border-gray-300'}`}
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(cat => (
                      <option key={cat._id || cat.id} value={cat._id || cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {errors.category && <p className="mt-1 text-xs text-red-600">{errors.category}</p>}
                </div>

                {/* ✅ REPLACED location by city select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville *</label>
                  <div className="relative">
                    <FiMapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <select
                      value={formData.city}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, city: e.target.value }));
                        setErrors(prev => ({ ...prev, city: undefined }));
                      }}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg ${
                        errors.city ? 'border-red-300' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Sélectionnez une ville</option>
                      {cities.map((city) => (
                        <option key={city._id} value={city._id}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.city && <p className="mt-1 text-xs text-red-600">{errors.city}</p>}
                </div>
              </div>

              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attribuer au Marchand (optionnel)
                  </label>
                  <select
                    name="merchantId"
                    value={formData.merchantId}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="">Aucun</option>
                    {merchants.map(m => (
                      <option key={m._id || m.id} value={m._id || m.id}>
                        {m.businessName || m.email || m._id}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Prix et Remise</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="number"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  placeholder={formData.productType === 'service' ? 'Prix original' : 'Prix avant remise (optionnel)'}
                  className={`px-4 py-2 border rounded-lg ${errors.originalPrice ? 'border-red-300' : 'border-gray-300'}`}
                />
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder={formData.productType === 'service' ? 'Prix deal' : 'Prix de vente'}
                  className={`px-4 py-2 border rounded-lg ${errors.price ? 'border-red-300' : 'border-gray-300'}`}
                />
                <div className="flex items-center h-11 px-4 bg-green-50 border border-green-200 rounded-lg">
                  <span className="text-lg font-bold text-green-600">-{discount}%</span>
                </div>
              </div>

              {/* PRIX PARTENAIRE — ce que Maxula reverse au partenaire.
                  Deux accords coexistent : la commission en pourcentage, et le
                  prix d'achat fixe (« donnez-nous 300 DH, vendez au prix que
                  vous voulez »). Laisser VIDE garde le mode commission : c'est
                  le defaut, aucun contrat existant n'est modifie. */}
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <label className="block text-sm font-semibold text-amber-900 mb-1">
                  Prix partenaire / prix d'achat (optionnel)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    name="partnerPrice"
                    value={formData.partnerPrice}
                    onChange={handleChange}
                    placeholder="Laisser vide = commission habituelle"
                    className="flex-1 px-4 py-2 border border-amber-300 rounded-lg bg-white"
                  />
                  <span className="text-sm font-semibold text-amber-900 shrink-0">MAD / unite</span>
                </div>
                <p className="text-xs text-amber-800 mt-2">
                  Montant verse au partenaire pour chaque unite vendue, quel que
                  soit le prix de vente. Vide = le partenaire est paye a la
                  commission de son pack.
                </p>
                {String(formData.partnerPrice).trim() !== '' && Number(formData.price) > 0 ? (
                  Number(formData.partnerPrice) > Number(formData.price) ? (
                    <p className="text-xs font-bold text-red-700 mt-2">
                      Attention : le prix d'achat depasse le prix de vente. Vous
                      perdriez {(Number(formData.partnerPrice) - Number(formData.price)).toFixed(2)} MAD par unite.
                    </p>
                  ) : (
                    <p className="text-xs font-bold text-green-700 mt-2">
                      Votre marge : {(Number(formData.price) - Number(formData.partnerPrice)).toFixed(2)} MAD par unite.
                    </p>
                  )
                ) : null}
                <p className="text-xs text-amber-700 mt-2">
                  Ce montant n'apparait jamais sur le site public ni sur le coupon.
                </p>
              </div>

              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.productType === 'service' ? 'Quantité' : 'Stock'}
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      name="quantity"
                      value={formData.unlimited ? '' : formData.quantity}
                      onChange={handleChange}
                      disabled={formData.unlimited}
                      placeholder={formData.unlimited ? 'Illimité' : (formData.productType === 'physical' ? 'Unités en stock' : 'Quantité')}
                      className={`flex-1 px-4 py-2 border rounded-lg ${formData.unlimited ? 'bg-gray-50 text-gray-400' : ''} ${errors.quantity ? 'border-red-300' : 'border-gray-300'}`}
                    />
                    <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        name="unlimited"
                        checked={formData.unlimited}
                        onChange={(e) => { const v = e.target.checked; setFormData((prev) => ({ ...prev, unlimited: v })); setErrors((prev) => ({ ...prev, quantity: undefined })); }}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Illimité
                    </label>
                  </div>
                  {!formData.unlimited && errors.quantity && <p className="mt-1 text-xs text-red-600">{errors.quantity}</p>}
                </div>
            </div>
          </div>

          {/* ===== Fiche produit (non-services) =====
              Ces informations n'ont de sens que pour un bien : un service n'est
              ni neuf ni d'occasion et n'a pas de date de péremption. */}
          {formData.productType !== 'service' && (
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="p-6 border-b">
                <h2 className="text-xl font-semibold text-gray-900">Fiche produit</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Ces informations rassurent l'acheteur et vous aident à gérer votre catalogue.
                </p>
              </div>
              <div className="p-6 space-y-5">

                {formData.productType === 'physical' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      État du produit
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {[
                        { key: 'new', label: 'Neuf' },
                        { key: 'used', label: 'Occasion' }
                      ].map((c) => (
                        <button
                          key={c.key}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, condition: c.key }))}
                          className={`px-5 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                            formData.condition === c.key
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Disponibilité
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      { key: 'in_stock', label: 'En stock' },
                      { key: 'out_of_stock', label: 'Rupture de stock' },
                      { key: 'incoming', label: 'En arrivage' }
                    ].map((a) => (
                      <button
                        key={a.key}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, availability: a.key }))}
                        className={`px-5 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                          formData.availability === a.key
                            ? 'border-primary-600 bg-primary-50 text-primary-700'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                  {formData.availability !== 'in_stock' && (
                    <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      L'annonce reste visible, mais les clients ne pourront pas la commander
                      tant que vous ne la repassez pas « En stock ».
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
                    <BrandPicker
                      value={{ brandId: formData.brandId, brand: formData.brand }}
                      onChange={(v) => setFormData((prev) => ({ ...prev, brandId: v.brandId, brand: v.brand }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Référence produit (SKU)
                    </label>
                    <input
                      type="text"
                      name="sku"
                      value={formData.sku}
                      onChange={handleChange}
                      maxLength={80}
                      placeholder="Votre référence interne"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {formData.productType === 'physical' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Code-barres <span className="text-gray-400 font-normal">(facultatif)</span>
                      </label>
                      <input
                        type="text"
                        name="barcode"
                        value={formData.barcode}
                        onChange={handleChange}
                        maxLength={40}
                        placeholder="EAN / UPC"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        6 à 32 caractères. Les espaces et tirets sont retirés automatiquement.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date de péremption
                      </label>
                      <input
                        type="date"
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={handleChange}
                        min={new Date().toISOString().slice(0, 10)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Compléments alimentaires, cosmétiques, produits frais. Laissez vide si
                        le produit ne périme pas.
                      </p>
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Dates (services uniquement) */}
          {formData.productType === 'service' && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Période de Validité</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de début *</label>
                  <input
                    type="date"
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg ${errors.validFrom ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  {errors.validFrom && <p className="mt-1 text-xs text-red-600">{errors.validFrom}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin {formData.openEnded ? '' : '*'}</label>
                  <input
                    type="date"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleChange}
                    disabled={formData.openEnded}
                    className={`w-full px-4 py-2 border rounded-lg ${errors.validUntil ? 'border-red-300' : 'border-gray-300'} ${formData.openEnded ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                  />
                  {errors.validUntil && <p className="mt-1 text-xs text-red-600">{errors.validUntil}</p>}
                </div>
              </div>
              {/* ✅ POINT 5 : validité ouverte */}
              <label className="mt-4 flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={formData.openEnded}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({ ...prev, openEnded: checked, validUntil: checked ? '' : prev.validUntil }));
                    setErrors(prev => ({ ...prev, validUntil: undefined }));
                  }}
                  className="h-4 w-4"
                />
                <span className="text-sm text-gray-700">Validité ouverte (sans date de fin)</span>
              </label>
            </div>
          </div>
          )}

          {/* ===== Livraison & variantes (produit physique) ===== */}
          {formData.productType === 'physical' && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Livraison &amp; Variantes</h2>
              <p className="text-sm text-gray-500 mt-1">Frais de livraison, seuil de gratuité et options (taille, couleur…).</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frais de livraison (MAD)</label>
                  <input
                    type="number" name="shippingFee" value={formData.shippingFee} onChange={handleChange}
                    placeholder="0 = gratuit"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Livraison gratuite dès (MAD)</label>
                  <input
                    type="number" name="freeShippingThreshold" value={formData.freeShippingThreshold} onChange={handleChange}
                    placeholder="Optionnel"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Poids (g)</label>
                  <input
                    type="number" name="weightGrams" value={formData.weightGrams} onChange={handleChange}
                    placeholder="Optionnel"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Lot D — Mode de tarif de livraison */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mode de tarif de livraison</label>
                <div className="flex gap-2">
                  {[{ k: 'flat', label: 'Tarif fixe' }, { k: 'weight', label: 'Au poids' }].map((m) => (
                    <button
                      key={m.k}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, shippingMode: m.k }))}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${formData.shippingMode === m.k ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                {formData.shippingMode === 'weight' && (
                  <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50/60">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Paliers de poids</span>
                      <button type="button" onClick={addTier} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"><FiPlus /> Ajouter un palier</button>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">Le tarif appliqué est celui du premier palier dont le poids « jusqu'à » couvre le poids total de la commande (poids unitaire × quantité). Au-delà du dernier palier, c'est le palier le plus lourd qui s'applique. Le poids unitaire est le champ « Poids (g) » ci-dessus.</p>
                    <div className="space-y-2">
                      {(formData.shippingWeightTiers || []).map((t, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="flex-1 flex items-center gap-1">
                            <span className="text-xs text-gray-500 whitespace-nowrap">jusqu'à</span>
                            <input type="number" value={t.uptoGrams} onChange={(e) => updateTier(i, 'uptoGrams', e.target.value)} placeholder="g" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                            <span className="text-xs text-gray-500">g</span>
                          </div>
                          <div className="flex-1 flex items-center gap-1">
                            <input type="number" value={t.fee} onChange={(e) => updateTier(i, 'fee', e.target.value)} placeholder="Frais" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                            <span className="text-xs text-gray-500">MAD</span>
                          </div>
                          <button type="button" onClick={() => removeTier(i)} className="text-gray-400 hover:text-red-600"><FiTrash2 /></button>
                        </div>
                      ))}
                      {(formData.shippingWeightTiers || []).length === 0 && (
                        <p className="text-xs text-gray-400">Aucun palier — le tarif fixe ci-dessus sera utilisé en repli.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Zones de livraison (optionnel)</label>
                  <button type="button" onClick={addZone} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"><FiPlus /> Ajouter une zone</button>
                </div>
                <p className="text-xs text-gray-400 mb-2">Frais spécifiques par ville. Les villes non listées utilisent le tarif par défaut ci-dessus.</p>
                <div className="space-y-3">
                  {(formData.shippingZones || []).map((z, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50/60 space-y-2">
                      <div className="flex items-center gap-2">
                        <input value={z.name} onChange={(e) => updateZone(i, 'name', e.target.value)} placeholder="Nom de la zone (ex. Grand Casablanca)" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                        <button type="button" onClick={() => removeZone(i)} className="text-gray-400 hover:text-red-600"><FiTrash2 /></button>
                      </div>
                      <input value={(z.cities || []).join(', ')} onChange={(e) => updateZoneCities(i, e.target.value)} placeholder="Villes séparées par des virgules — ex. Casablanca, Mohammedia" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      <div className="grid grid-cols-2 gap-2">
                        <input type="number" value={z.fee} onChange={(e) => updateZone(i, 'fee', e.target.value)} placeholder="Frais (MAD)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                        <input type="number" value={z.freeThreshold} onChange={(e) => updateZone(i, 'freeThreshold', e.target.value)} placeholder="Gratuit dès (MAD)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Caractéristiques du produit</label>
                <p className="text-xs text-gray-400 mb-3">Activez ce qui correspond à votre produit. Les bons réglages s'affichent, puis générez les variantes automatiquement.</p>

                {/* Choix des caractéristiques applicables */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {ATTR_ORDER.map((type) => {
                    const active = !!getAttr(type);
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleAttribute(type)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${active ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                      >
                        {active ? '✓ ' : '+ '}{ATTR_DEFS[type].label}
                      </button>
                    );
                  })}
                </div>

                {/* Éditeur par caractéristique active */}
                <div className="space-y-3">
                  {(formData.attributes || []).map((a) => {
                    const def = ATTR_DEFS[a.type];
                    if (!def) return null;
                    return (
                      <div key={a.type} className="border border-gray-200 rounded-lg p-3 bg-gray-50/60">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-gray-800">{def.label}</span>
                            {def.units && (
                              <select
                                value={a.unit || def.units[0]}
                                onChange={(e) => setAttrUnit(a.type, e.target.value)}
                                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                              >
                                {def.units.map((u) => <option key={u} value={u}>{u}</option>)}
                              </select>
                            )}
                          </div>
                          <button type="button" onClick={() => toggleAttribute(a.type)} className="text-gray-400 hover:text-red-600" title="Retirer">
                            <FiX />
                          </button>
                        </div>

                        {/* Presets rapides */}
                        {def.presets && def.presets.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {def.presets.map((p) => {
                              const already = a.values.includes(p);
                              return (
                                <button
                                  key={p}
                                  type="button"
                                  onClick={() => already ? removeAttrValue(a.type, p) : addAttrValue(a.type, p)}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border transition ${already ? 'border-primary-500 bg-primary-100 text-primary-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100'}`}
                                >
                                  {def.kind === 'color' && (
                                    <span className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: COLOR_HEX[p] || '#ddd' }} />
                                  )}
                                  {def.kind === 'measure' ? `${p}${a.unit || ''}` : p}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Saisie libre */}
                        <div className="flex gap-2 mb-2">
                          <input
                            value={attrInputs[a.type] || ''}
                            onChange={(e) => setAttrInputs((s) => ({ ...s, [a.type]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAttrValue(a.type, attrInputs[a.type]); setAttrInputs((s) => ({ ...s, [a.type]: '' })); } }}
                            placeholder={def.ph || 'Ajouter une valeur'}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                          />
                          <button
                            type="button"
                            onClick={() => { addAttrValue(a.type, attrInputs[a.type]); setAttrInputs((s) => ({ ...s, [a.type]: '' })); }}
                            className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:opacity-90"
                          >
                            Ajouter
                          </button>
                        </div>

                        {/* Valeurs sélectionnées */}
                        {a.values.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {a.values.map((v) => (
                              <span key={v} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-300 text-xs text-gray-700">
                                {def.kind === 'color' && (
                                  <span className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: COLOR_HEX[v] || '#ddd' }} />
                                )}
                                {def.kind === 'measure' ? `${v}${a.unit || ''}` : v}
                                <button type="button" onClick={() => removeAttrValue(a.type, v)} className="text-gray-400 hover:text-red-600">
                                  <FiX size={12} />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {(formData.attributes || []).some(a => a.values && a.values.length) && (
                  <button
                    type="button"
                    onClick={generateVariants}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
                  >
                    <FiPackage /> Générer les variantes
                  </button>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Variantes &amp; prix / stock</label>
                  <button type="button" onClick={addVariant} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
                    <FiPlus /> Ajouter une variante
                  </button>
                </div>
                {(formData.variants || []).length === 0 && (
                  <p className="text-xs text-gray-400">Générez les variantes ci-dessus, ou ajoutez-les manuellement. Renseignez le supplément de prix et le stock par variante.</p>
                )}
                <div className="space-y-2">
                  {(formData.variants || []).map((v, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start">
                      <input
                        value={v.label} onChange={(e) => updateVariant(i, 'label', e.target.value)}
                        placeholder="Libellé (ex. 80x200cm)"
                        className={`col-span-6 px-3 py-2 border rounded-lg ${errors[`variant_${i}`] ? 'border-red-300' : 'border-gray-300'}`}
                      />
                      <input
                        type="number" value={v.priceDelta} onChange={(e) => updateVariant(i, 'priceDelta', e.target.value)}
                        placeholder="+/- prix"
                        className="col-span-3 px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="number" value={v.stock} onChange={(e) => updateVariant(i, 'stock', e.target.value)}
                        placeholder="Stock"
                        className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <button type="button" onClick={() => removeVariant(i)} className="col-span-1 h-10 flex items-center justify-center text-gray-400 hover:text-red-600">
                        <FiTrash2 />
                      </button>
                      {errors[`variant_${i}`] && <p className="col-span-12 text-xs text-red-600">{errors[`variant_${i}`]}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* ===== Livraison digitale ===== */}
          {formData.productType === 'digital' && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Livraison digitale</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                <FiDownloadCloud className="text-indigo-600 mt-0.5 shrink-0" />
                <p className="text-sm text-indigo-800">
                  Le produit digital n'est pas téléchargeable directement : après paiement, vous le livrez à l'acheteur
                  via la <strong>messagerie de commande sécurisée</strong> (partage du lien). Un journal de traçabilité
                  est conservé pendant la fenêtre de traçabilité définie par la plateforme.
                </p>
              </div>
            </div>
          </div>
          )}

          {/* Highlights */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b flex justify-between">
              <h2 className="text-xl font-semibold">Points Forts</h2>
              <button
                type="button"
                onClick={addHighlight}
                className="px-3 py-1 bg-primary-600 text-white rounded"
              >
                Ajouter
              </button>
            </div>
            <div className="p-6 space-y-3">
              {formData.highlights.map((h, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <input
                    value={h}
                    onChange={(e) => handleHighlightChange(i, e.target.value)}
                    className="flex-1 px-4 py-2 border rounded-lg"
                  />
                  {formData.highlights.length > 1 && (
                    <button type="button" onClick={() => removeHighlight(i)} className="p-2 text-red-600">
                      <FiX />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Images */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Images</h2></div>
            <div className="p-6">
              <div>
                <p className="text-xs text-gray-500 mb-2">{'Recommandé : 880 × 340 px, < 300 Ko'}</p>
                <input id="image-upload" type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
                <label htmlFor="image-upload" className="block border-2 border-dashed border-indigo-400 bg-indigo-50 rounded-xl p-8 text-center cursor-pointer hover:bg-indigo-100 hover:border-indigo-500 transition">
                  <FiUploadCloud className="mx-auto text-indigo-500 mb-2" size={38} />
                  <p className="text-sm font-medium text-indigo-700">Glissez ou choisissez (max {MAX_IMAGES})</p>
                  <span className="mt-3 inline-block px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg pointer-events-none">Choisir des images</span>
                </label>
                {errors.images && <p className="mt-2 text-xs text-red-600">{errors.images}</p>}

                {(formData.images || []).length > 0 && (
                  <>
                  <p className="mt-4 text-xs text-gray-500">Glissez pour réordonner (ou utilisez les flèches au survol). La 1ʳᵉ image est l'image principale du produit.</p>
                  <div className="mt-2 grid grid-cols-3 gap-3">
                    {(formData.images || []).map((file, idx) => {
                      let url = imagePreviewsRef.current[idx];
                      if (!url && file) {
                        try { url = URL.createObjectURL(file); imagePreviewsRef.current[idx] = url; } catch (e) { url = ''; }
                      }
                      return (
                        <div
                          key={idx}
                          draggable
                          onDragStart={() => setDragIdx(idx)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => { e.preventDefault(); reorderImages(dragIdx, idx); setDragIdx(null); }}
                          onDragEnd={() => setDragIdx(null)}
                          className={`relative border rounded overflow-hidden group bg-white cursor-grab active:cursor-grabbing transition ${dragIdx === idx ? 'opacity-40 ring-2 ring-primary-400' : 'hover:ring-1 hover:ring-gray-300'}`}
                        >
                          {url
                            ? <img src={url} alt={`preview-${idx}`} draggable={false} className="w-full h-32 object-cover select-none" />
                            : <div className="w-full h-32 bg-gray-100 flex items-center justify-center">No image</div>}
                          <span className={`absolute top-1 left-1 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${idx === 0 ? 'bg-primary-600 text-white' : 'bg-black/55 text-white'}`}>
                            {idx === 0 ? <><FiStar size={10} /> Principale</> : idx + 1}
                          </span>
                          <button type="button" onClick={() => removeImageAt(idx)} title="Supprimer" className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-600 shadow-sm hover:bg-red-50">
                            <FiX />
                          </button>
                          <div className="absolute bottom-1 inset-x-1 flex justify-between opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                            <button type="button" disabled={idx === 0} onClick={() => reorderImages(idx, idx - 1)} title="Déplacer avant" aria-label="Déplacer avant" className="bg-white/95 rounded-full p-1 text-gray-700 shadow-sm disabled:opacity-30 hover:bg-white"><FiChevronLeft size={14} /></button>
                            <button type="button" disabled={idx === (formData.images || []).length - 1} onClick={() => reorderImages(idx, idx + 1)} title="Déplacer après" aria-label="Déplacer après" className="bg-white/95 rounded-full p-1 text-gray-700 shadow-sm disabled:opacity-30 hover:bg-white"><FiChevronRight size={14} /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </>
                )}

                {(() => {
                  const colorAttr = (formData.attributes || []).find((a) => a.type === 'color' && Array.isArray(a.values) && a.values.length);
                  const imgs = formData.images || [];
                  if (!colorAttr || imgs.length === 0) return null;
                  const previewUrl = (idx) => {
                    let u = imagePreviewsRef.current[idx];
                    if (!u && imgs[idx]) { try { u = URL.createObjectURL(imgs[idx]); imagePreviewsRef.current[idx] = u; } catch (e) { u = ''; } }
                    return u || '';
                  };
                  return (
                    <div className="mt-6 border-t pt-4">
                      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
                        <label className="block text-sm font-medium text-gray-700">Image par couleur (optionnel)</label>
                        <div className="flex items-center gap-2 flex-wrap">
                          {lowConfColors.length > 0 && (
                            <button type="button" onClick={() => { clearColorImages(lowConfColors); setLowConfColors([]); }}
                              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 whitespace-nowrap" title="Retirer toutes les associations marquées incertaines">
                              <FiAlertTriangle size={13} /> Effacer les incertaines ({lowConfColors.length})
                            </button>
                          )}
                          {Object.keys(formData.colorImageMap || {}).length > 0 && (
                            <button type="button" onClick={() => { clearColorImages(); setLowConfColors([]); }}
                              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 whitespace-nowrap" title="Retirer toutes les associations">
                              <FiTrash2 size={13} /> Tout effacer
                            </button>
                          )}
                          <button type="button" onClick={autoDetectColors} disabled={detecting}
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100 disabled:opacity-60 whitespace-nowrap">
                            {detecting ? <FiLoader className="animate-spin" size={13} /> : <FiZap size={13} />}
                            {detecting ? 'Analyse…' : 'Détecter automatiquement'}
                          </button>
                        </div>
                      </div>
                      {detecting && (
                        <div className="mb-3">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1"><span>Analyse des images…</span><span>{detectProgress}%</span></div>
                          <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-primary-600 transition-all duration-300" style={{ width: `${detectProgress}%` }} /></div>
                        </div>
                      )}
                      <p className="text-xs text-gray-400 mb-3">Associez une photo à chaque couleur : sur la fiche produit, choisir la couleur affichera automatiquement cette image. « Détecter automatiquement » analyse la couleur dominante de chaque image et propose l'association.</p>
                      <div className="space-y-3">
                        {colorAttr.values.map((c, ci) => (
                          <div key={ci} className="flex items-start gap-3 flex-wrap">
                            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 w-32 pt-1">
                              <span className="w-4 h-4 rounded-full border border-gray-300 shrink-0" style={{ backgroundColor: COLOR_HEX[c] || '#ddd' }} />
                              {c}
                              {lowConfColors.includes(c) && <FiAlertTriangle size={12} className="text-amber-500 shrink-0" title="Couleur incertaine — vérifiez l'association" />}
                            </span>
                            <div className="flex flex-wrap gap-2 flex-1">
                              {imgs.map((file, idx) => {
                                const sel = Number(formData.colorImageMap?.[c]) === idx;
                                return (
                                  <button key={idx} type="button" onClick={() => setColorImage(c, idx)} title={`Image ${idx + 1}`}
                                    className={`w-12 h-12 rounded-md overflow-hidden border-2 transition ${sel ? 'border-primary-600 ring-2 ring-primary-200' : 'border-gray-200 hover:border-gray-300'}`}>
                                    {previewUrl(idx)
                                      ? <img src={previewUrl(idx)} alt={`img-${idx}`} className="w-full h-full object-cover" />
                                      : <span className="text-[10px] flex items-center justify-center w-full h-full">{idx + 1}</span>}
                                  </button>
                                );
                              })}
                              {formData.colorImageMap?.[c] != null && (
                                <button type="button" onClick={() => setColorImage(c, null)} title="Retirer l'association de cette couleur"
                                  className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded self-center">
                                  <FiX size={12} /> Retirer
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          {/* Validation admin obligatoire : le marchand doit savoir que sa publication
              n'est pas immédiate, sinon il croit à un bug quand l'annonce n'apparaît pas. */}
          {!isAdmin && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-900">
                <strong>Validation avant publication :</strong> votre annonce sera contrôlée
                par un administrateur avant d'être visible sur la plateforme. Vous recevrez
                une notification dès qu'elle est validée, ou le motif du refus s'il y a lieu.
              </p>
            </div>
          )}

          {/* ===== Lot P1 — Infos de prestation (communiquées sur le coupon) ===== */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Infos de prestation</h2>
              <p className="text-sm text-gray-500 mt-1">
                Ces informations aident le client à utiliser sa prestation. L'adresse exacte et
                les consignes ne sont <strong>jamais affichées publiquement</strong> : elles lui
                sont communiquées sur son coupon, uniquement <strong>après paiement</strong>.
              </p>
            </div>
            <div className="p-6 space-y-4">
              {!isAdmin && (
                <ServiceLocationPicker
                  value={formData.serviceLocationId}
                  onApply={applyServiceLocation}
                  autoSelectDefault
                />
              )}

              {/* MULTI-VILLES : coche les lieux ou cette annonce est
                  disponible. Le lieu principal ci-dessus reste celui qui
                  alimente l'adresse et les horaires de la fiche ; ceux coches
                  ici s'y ajoutent, et le client choisira le sien a l'achat. */}
              <DealCitiesPicker
                value={formData.serviceLocationIds || []}
                onChange={(ids) => setFormData((p) => ({ ...p, serviceLocationIds: ids }))}
              />
              <div>
                <label className="block text-sm font-medium mb-1">
                  Adresse exacte {formData.productType === 'service' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  name="addressFull"
                  value={formData.addressFull}
                  onChange={handleChange}
                  readOnly={locked}
                  placeholder="Ex : 12 Rue Beethoven, Résidence Oumnia, Bureau 83, Tanger"
                  className={`w-full px-4 py-2 border rounded-lg ${locked ? 'bg-gray-100 text-gray-600' : ''}`}
                />
                {errors.addressFull && <p className="text-red-500 text-sm mt-1">{errors.addressFull}</p>}
                <p className="text-xs text-gray-500 mt-1">Adresse où le client se présente. Communiquée après l'achat.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Horaires d'ouverture</label>
                  <input
                    type="text"
                    name="openingHours"
                    value={formData.openingHours}
                    onChange={handleChange}
                    readOnly={locked}
                    placeholder="Ex : Lun–Sam 10h–19h"
                    className={`w-full px-4 py-2 border rounded-lg ${locked ? 'bg-gray-100 text-gray-600' : ''}`}
                  />
                  <p className="text-xs text-gray-500 mt-1">Affiché publiquement sur la fiche.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Durée de la prestation</label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      name="durationMinutes"
                      value={formData.durationMinutes}
                      onChange={handleChange}
                      placeholder="60"
                      className="w-full px-4 py-2 border rounded-lg pr-14"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">min</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Latitude (facultatif)</label>
                  <input
                    type="text"
                    name="addressLat"
                    value={formData.addressLat}
                    onChange={handleChange}
                    readOnly={locked}
                    placeholder="35.7595"
                    className={`w-full px-4 py-2 border rounded-lg ${locked ? 'bg-gray-100 text-gray-600' : ''}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Longitude (facultatif)</label>
                  <input
                    type="text"
                    name="addressLng"
                    value={formData.addressLng}
                    onChange={handleChange}
                    readOnly={locked}
                    placeholder="-5.8340"
                    className={`w-full px-4 py-2 border rounded-lg ${locked ? 'bg-gray-100 text-gray-600' : ''}`}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-2">
                Coordonnées GPS : permettent au client d'ouvrir l'itinéraire depuis son coupon.
              </p>

              <div>
                <label className="block text-sm font-medium mb-1">Consignes de présentation</label>
                <textarea
                  name="instructions"
                  value={formData.instructions}
                  onChange={handleChange}
                  rows="3"
                  readOnly={locked}
                  placeholder="Ex : Se présenter 10 minutes avant, demander l'accueil au 1er étage, munissez-vous de votre coupon."
                  className={`w-full px-4 py-2 border rounded-lg ${locked ? 'bg-gray-100 text-gray-600' : ''}`}
                />
                <p className="text-xs text-gray-500 mt-1">Communiquées au client sur son coupon, après paiement.</p>
              </div>

              {/* Saisie unique : mémoriser ce lieu pour les prochaines annonces */}
              {!isAdmin && !formData.serviceLocationId && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="saveAsServiceLocation"
                      checked={!!formData.saveAsServiceLocation}
                      onChange={(e) => setFormData((f) => ({ ...f, saveAsServiceLocation: e.target.checked }))}
                      className="mt-1"
                    />
                    <span className="text-sm text-gray-800">
                      Enregistrer ce lieu dans mon profil pour le réutiliser
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Vos prochaines annonces reprendront automatiquement cette adresse, ces horaires
                        et ces consignes. Vous n'aurez plus à les ressaisir.
                      </span>
                    </span>
                  </label>
                  {formData.saveAsServiceLocation && (
                    <input
                      type="text"
                      name="serviceLocationLabel"
                      value={formData.serviceLocationLabel}
                      onChange={handleChange}
                      placeholder="Nom du lieu (ex : Salon Tanger Centre)"
                      className="mt-3 w-full px-4 py-2 border rounded-lg bg-white"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Conditions</h2></div>
            <div className="p-6">
              <textarea
                name="terms"
                value={formData.terms}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
          </div>

          {/* Prix dégressif : juste avant les boutons, après le prix et le stock —
              on ne peut le régler qu'une fois le prix unitaire connu, sinon
              l'aperçu « soit X MAD l'unité » n'a rien à calculer. */}
          <QuantityTiersEditor
            value={formData.quantityTiers}
            onChange={(v) => setFormData((f) => ({ ...f, quantityTiers: v }))}
            unitPrice={Number(formData.price) || 0}
          />

          <InfoSectionsEditor
            value={formData.infoSections}
            onChange={(v) => setFormData((f) => ({ ...f, infoSections: v }))}
          />

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(isAdmin ? '/admin/deals' : '/merchant/deals')}
              className="px-6 py-3 border rounded"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-6 py-3 bg-primary-600 text-white rounded"
            >
              {loading
                ? (uploadProgress !== null ? `Envoi ${uploadProgress}%` : 'Envoi...')
                : (<><FiSave className="mr-2" />Créer</>)
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDeal;